<template>
  <MetaLoader @ready="isMetaReady = true">
    <div style="display: contents">
      <Teleport to="#top-menu-bar" defer>
        <div v-show="!isLeaving" class="layout__top-bar">
          <WizardMap
            v-if="wizardMapSteps.length"
            :steps="wizardMapSteps"
            :current-step-index="currentStepIndex"
            :previous-step="previousStep"
            :loading="!showContent"
            @step-back="navigateBack"
            @step-to="(name) => navigateTo({ name, query: $route.query })"
          />
        </div>
      </Teleport>
      <div style="overflow: hidden; height: 100%">
        <router-view
          v-if="isMetaReady && isMountReady && !isLeaving"
          v-show="showContent"
          v-bind="{
            wizardRoutes,
            firstStep,
            firstStepRoute,
            previousStep,
            previousStepRoute,
            next: nextStep,
            nextRoute: nextStepRoute,
            currentStepIndex,
          }"
          @is-loading="(l) => (isLoading = l)"
        />
        <div
          v-if="!showContent"
          class="is-flex is-align-items-center is-justify-content-center"
          style="height: 100%; width: 100%; margin: 2rem auto"
        >
          <span class="icon">
            <i class="fa fa-spinner fa-spin fa-2x" />
          </span>
        </div>
      </div>
      <NavGuard
        title="Leaving the wizard"
        :disabled="navGuardDisabled"
        :nav-next="guardedNavNext"
        :nav-cancel="guardedNavCancel"
      >
        <template #body>
          You are about to leave the data registration wizard. Your changes will
          be lost
        </template>
      </NavGuard>
    </div>
  </MetaLoader>
</template>

<script>
import { onMounted } from 'vue'
import { isEqual } from 'lodash-es'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'

import MetaLoader from '@/components/MetaLoader.vue'
import NavGuard from '@/components/NavGuard.vue'
import WizardMap from '@/components/data-loader/WizardMap.vue'
import { useMenuToggles } from '@/composables/layouts/useMenuToggles'
import { usePageShell } from '@/composables/usePageShell'

export default {
  name: 'WizardView',
  components: {
    MetaLoader,
    NavGuard,
    WizardMap,
  },
  props: {
    managed: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    usePageShell({ title: 'Dataset Loader' })
    const { closeLeftNav } = useMenuToggles()
    closeLeftNav()
    onMounted(() => closeLeftNav())
  },
  data() {
    return {
      routeName: this.$route.name || null,
      isMetaReady: false,
      isMountReady: false,
      isLoading: true,
      isLeaving: false,
      navGuardDisabled: true,
      guardedNavNext: null,
      guardedNavCancel: null,
    }
  },
  computed: {
    ...mapState('dataloader/assay', {
      storeReadOnlyAssay: 'readOnlyAssay',
      storeRegisterToAssay: 'registerToAssay',
    }),
    ...mapGetters('dataloader/main', ['studyOptions']),
    ...mapGetters('dataloader/dropbox', {
      selectedFilesCount: 'filesCount',
      hasInvalidCharacterFiles: 'hasInvalidCharacterFiles',
    }),
    ...mapGetters('dataloader/assay', { assayType: 'resolvedAssayType' }),
    ...mapGetters('dataloader/collections', ['hasAssignSamplesCollection']),
    currentRouteChildren() {
      let allRoutes = this.$router.options.routes
      let currentRoutePath = this.$route.matched[0].path
      return allRoutes.find((r) => r.path === currentRoutePath)?.children || []
    },
    wizardRoutes() {
      return this.currentRouteChildren.filter((route) => {
        const skipAssay = !this.storeRegisterToAssay || this.storeReadOnlyAssay
        const discard =
          route.path === '' ||
          (skipAssay && route.meta?.id === 'select-assay-type') ||
          (skipAssay && route.meta?.id === 'assay-details')
        return !discard
      })
    },
    wizardSteps() {
      return this.wizardRoutes.map(this.getStep)
    },
    stepMax() {
      return this.wizardSteps.length
    },
    wizardMapSteps() {
      // Here we filter again so that the last step (assign samples) does not
      // appear if not needed. It is not directly filtered from wizardRoutes
      // because we need to navigate to the assign sample page, since this
      // is where the payload is formatted...
      // A better way would be to compute payload outside of the assign sample
      // page so that it can be submitted directly from the verify view.
      // However, currently, two distinct payload are needed (lagacy + new) so
      // this is too annoying to implement and is for now postponed.
      return this.wizardRoutes
        .map(this.getStep)
        .filter((route) => {
          const doAssignSamples =
            this.storeRegisterToAssay || this.hasAssignSamplesCollection
          const discard = !doAssignSamples && route.id === 'assign-samples'
          return !discard
        })
        .map((step) => {
          // Add warning state to select-data step if there are invalid file names
          if (step.id === 'select-data') {
            return {
              ...step,
              hasWarning: this.hasInvalidCharacterFiles,
              message: 'Some selected file names contain invalid characters',
            }
          }
          return step
        })
    },
    currentStepIndex() {
      return this.wizardSteps.findIndex((s) => s.name === this.routeName)
    },
    selectDataStepIndex() {
      return this.wizardSteps.findIndex((s) => s.id === 'select-data')
    },
    firstStep() {
      return this.wizardSteps[0]
    },
    firstStepRoute() {
      const name = this.firstStep?.name
      return { name, query: this.$route.query }
    },
    previousStep() {
      const previousStepIndex = this.currentStepIndex - 1
      return previousStepIndex >= 0 ? this.wizardSteps[previousStepIndex] : null
    },
    previousStepRoute() {
      const name = this.previousStep?.name
      return { name, query: this.$route.query }
    },
    nextStep() {
      const nextStepIndex = this.currentStepIndex + 1
      return nextStepIndex < this.stepMax
        ? this.wizardSteps[nextStepIndex]
        : null
    },
    nextStepRoute() {
      const name = this.nextStep?.name
      return { name, query: this.$route.query }
    },
    showContent() {
      return this.isMetaReady && !this.isLoading && !this.isLeaving
    },
  },
  methods: {
    ...mapMutations('dataloader/main', {
      resetMain: 'RESET_STUDY_OPTIONS',
    }),
    ...mapMutations('dataloader/assay', {
      setRegisterToAssay: 'SET_REGISTER_TO_ASSAY',
      setReadOnlyAssay: 'SET_READONLY_ASSAY',
    }),

    ...mapActions('dataloader/assay', {
      resetAssay: 'reset',
      setAssayData: 'setAssayData',
    }),
    ...mapActions('dataloader/collections', { resetCollections: 'reset' }),
    ...mapActions('dataloader/datasets', { resetDatasets: 'reset' }),
    ...mapActions('dataloader/dropbox', { resetDropbox: 'reset' }),
    getStep(r) {
      return {
        ...r,
        ...r.meta,
      }
    },
    async navigateTo(to) {
      await this.$router.push(to)
    },
    navigateBack() {
      this.navigateTo(this.previousStepRoute)
    },
    async checkQueryReplace(query) {
      let redirect = false
      const queryModified = !isEqual(this.$route.query, query)
      if (queryModified) {
        redirect = true
        await this.$router.replace({ ...this.$route, query })
      }
      return redirect
    },
    async checkRedirect() {
      let redirect = false
      const lostInSteps = this.currentStepIndex === -1
      const missingAssayType =
        this.storeRegisterToAssay &&
        this.currentStepIndex >= 1 &&
        !this.assayType
      const missingFiles =
        !this.managed &&
        this.currentStepIndex > this.selectDataStepIndex &&
        this.selectedFilesCount <= 0
      const missingStudyOptions =
        this.managed &&
        this.currentStepIndex >= 1 &&
        this.studyOptions.length <= 0
      if (
        lostInSteps ||
        missingAssayType ||
        missingFiles ||
        missingStudyOptions
      ) {
        redirect = true
        await this.$router
          .replace({
            ...this.firstStepRoute,
            params: this.$route.params,
            query: this.$route.query,
          })
          .catch(() => {})
      }
      return redirect
    },
    checkNavGuards(to) {
      const navWithinWizard =
        this.wizardSteps.findIndex((s) => s.name === to.name) !== -1
      const navToSuccess =
        this.managed === false && to?.meta?.id === 'registration-success'
      return { navWithinWizard, navToSuccess }
    },
    resetWizard() {
      this.resetMain()
      this.resetAssay()
      this.resetCollections()
      this.resetDatasets()
      this.resetDropbox()
    },
    doLeaveWizard() {
      this.isLeaving = true
      this.$nextTick(() => this.resetWizard())
    },
    async processQuery(query) {
      if (query.success) delete query.success
      if (query.reset) {
        delete query.reset
        // DO RESET STORE
        this.resetWizard()
      }
      if (query.raw) {
        this.setRegisterToAssay(true)
      }
      if (query.assayId) {
        const assayId = query.assayId
        this.setRegisterToAssay(true)
        this.setReadOnlyAssay(true)
        await this.setAssayData(assayId)
      }
      if (query.templateId && this.routeName !== 'assayTypeSelect') {
        delete query.templateId
      }
      return query
    },
    async initialise() {
      const query = await this.processQuery({ ...this.$route.query })
      await this.checkQueryReplace(query)
      await this.checkRedirect()
      this.isMountReady = true
      this.isLoading = false
    },
  },
  mounted() {
    this.initialise()
  },
  beforeRouteUpdate(to) {
    const { navWithinWizard } = this.checkNavGuards(to)
    if (navWithinWizard || this.selectedFilesCount === 0) {
      this.navGuardDisabled = true
      return
    }
    // Show confirmation modal, block navigation until resolved
    this.navGuardDisabled = false
    return new Promise((resolve) => {
      this.guardedNavNext = () => {
        this.navGuardDisabled = true
        this.doLeaveWizard()
        resolve()
      }
      this.guardedNavCancel = () => resolve(false)
    })
  },
  beforeRouteLeave(to) {
    /* Leaving wizard */
    const { navToSuccess } = this.checkNavGuards(to)
    const canNavOut =
      // for managed assay, allow nav out without restriction
      this.managed ||
      // for unmanaged assay, allow nav out when no file selected
      (!this.managed && this.selectedFilesCount === 0)
    if (navToSuccess || canNavOut) {
      this.navGuardDisabled = true
      this.doLeaveWizard()
      return
    }
    // Show confirmation modal, block navigation until resolved
    this.navGuardDisabled = false
    return new Promise((resolve) => {
      this.guardedNavNext = () => {
        this.navGuardDisabled = true
        this.doLeaveWizard()
        resolve()
      }
      this.guardedNavCancel = () => resolve(false)
    })
  },
}
</script>
